/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1702252800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Dec 11 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "facebook-catalog",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Connect to Facebook Product Catalog",
          "depth": 0,
          "id": "connect-to-facebook-product-catalog"
        }, {
          "value": "Authenticate with a system user token",
          "depth": 1,
          "id": "authenticate-with-a-system-user-token"
        }, {
          "value": "Authenticate with OAuth",
          "depth": 1,
          "id": "authenticate-with-oauth"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing products",
          "depth": 1,
          "id": "syncing-products"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mappings",
          "depth": 1,
          "id": "field-mappings"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      a: "a",
      img: "img",
      strong: "strong",
      code: "code",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Products"
            }), _jsx(_components.td, {
              children: "Sync records to products in your catalogs."
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't have a product catalog already, navigate to Facebook ", _jsx(_components.a, {
          href: "https://business.facebook.com/commerce/",
          children: "Commerce Manager"
        }), " and click \"Add Catalog\" to create one."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-facebookAdCatalog-1.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-facebook-product-catalog",
        children: "Connect to Facebook Product Catalog"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Facebook Product Catalog"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ".\nYou can then authenticate Hightouch to your Facebook Product Catalog either with a system user token or with OAuth."]
      }), "\n", _jsx(_components.p, {
        children: "Since the OAuth flow requires you to manually refresh this connection every 60 days, we strongly recommend using a system user token for indefinite access."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Make sure to select an ", _jsx(_components.strong, {
            children: "Ad Account"
          }), " on the destination configuration page.\nOtherwise, your sync fails with an ", _jsx(_components.a, {
            href: "#unsupported-post-request-object-with-id-undefined-does-not-exist",
            children: _jsx(_components.code, {
              children: "'undefined' does not   exist"
            })
          }), "\nerror."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-a-system-user-token",
        children: "Authenticate with a system user token"
      }), "\n", _jsxs(_components.p, {
        children: ["When authenticating with a system user token you can choose to either use an existing one or create a new one for the Hightouch integration.\nTo use an ", _jsx(_components.a, {
          href: "#use-an-existing-system-user-token",
          children: "existing system user token"
        }), ", you must meet the following prerequisites:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The system user whose token you'd like to use has ", _jsx(_components.code, {
            children: "Admin"
          }), " access."]
        }), "\n", _jsx(_components.li, {
          children: "You've created an app to add as an assigned asset to the system user."
        }), "\n", _jsx(_components.li, {
          children: "You've assigned the ad account where your catalog lives for to the system user."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-oauth",
        children: "Authenticate with OAuth"
      }), "\n", _jsxs(_components.p, {
        children: ["For the ", _jsx(_components.strong, {
          children: "Authentication method"
        }), ", select ", _jsx(_components.strong, {
          children: "Log in to Facebook"
        }), " and log into your Facebook account."]
      }), "\n", _jsx(_components.p, {
        children: "Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Facebook Product Catalog destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data.\nGo to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Facebook Product Catalog destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-products",
        children: "Syncing products"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Facebook catalog products:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Product items"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Vehicles"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsx(_components.p, {
        children: "Product records can be matched from your source to your Facebook catalog workspace by the product ID in the Upsert mode."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-facebookAdCatalog-2.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mappings",
        children: "Field mappings"
      }), "\n", _jsx(_components.p, {
        children: "You can sync columns from your source to Facebook catalog's default and custom fields. Hightouch automatically detects existing custom fields from your Facebook catalog account."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-facebookAdCatalog-3.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Review the ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/catalog-batch/reference#supported-fields-items-batch",
          children: "documentation"
        }), " on what fields are required to sync to your catalog type."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
